/**
 * Guest Order History Helpers
 * Manages order history in localStorage for guest users
 */

import { sendGuestOrderConfirmationEmail } from '../constant';

/**
 * Save guest order to localStorage
 * Called after successful payment
 */
export function saveGuestOrder(orderData) {
  try {
    const guestOrders = JSON.parse(localStorage.getItem('guest_orders') || '[]');
    
    const newOrder = {
      order_id: orderData.order_id || Date.now().toString(),
      order_id_main: orderData.order_id_main || orderData.order_id,
      transaction_id: orderData.transaction_id || '',
      status: orderData.status || 'Success',
      total_price: orderData.total_price || '0',
      created_on: orderData.created_on || new Date().toISOString(),
      payment_mode: orderData.payment_mode || 'Online',
      
      // Products
      products: orderData.products || [],
      
      // Magazines
      magazines: orderData.magazines || [],
      
      // Events
      events: orderData.events || [],
      
      // Machine Test Events
      machineTestEvents: orderData.machineTestEvents || [],
      
      // Addresses
      billing_address: orderData.billing_address || {},
      shipping_address: orderData.shipping_address || {},
      
      // Additional details
      delivery_type: orderData.delivery_type || '1',
      shipping_charges: orderData.shipping_charges || '0',
      
      // Timestamp
      saved_at: new Date().toISOString()
    };
    
    // Add to beginning of array (most recent first)
    guestOrders.unshift(newOrder);
    
    // Keep only last 10 orders to avoid localStorage limit
    const limitedOrders = guestOrders.slice(0, 10);
    
    localStorage.setItem('guest_orders', JSON.stringify(limitedOrders));
    
    console.log('Guest order saved to localStorage:', newOrder);
    return true;
  } catch (error) {
    console.error('Error saving guest order:', error);
    return false;
  }
}

/**
 * Get all guest orders from localStorage
 */
export function getGuestOrders() {
  try {
    const orders = JSON.parse(localStorage.getItem('guest_orders') || '[]');
    console.log('Retrieved guest orders:', orders);
    return orders;
  } catch (error) {
    console.error('Error retrieving guest orders:', error);
    return [];
  }
}

/**
 * Get single guest order by order_id
 */
export function getGuestOrderById(order_id) {
  try {
    const orders = getGuestOrders();
    const order = orders.find(o => o.order_id == order_id || o.order_id_main == order_id);
    console.log('Found guest order:', order);
    return order || null;
  } catch (error) {
    console.error('Error retrieving guest order by ID:', error);
    return null;
  }
}

/**
 * Clear all guest orders from localStorage
 */
export function clearGuestOrders() {
  try {
    localStorage.removeItem('guest_orders');
    console.log('Guest orders cleared from localStorage');
    return true;
  } catch (error) {
    console.error('Error clearing guest orders:', error);
    return false;
  }
}

/**
 * Get guest orders by status
 */
export function getGuestOrdersByStatus(status) {
  try {
    const orders = getGuestOrders();
    return orders.filter(order => 
      order.status.toLowerCase() === status.toLowerCase()
    );
  } catch (error) {
    console.error('Error filtering guest orders by status:', error);
    return [];
  }
}

/**
 * Format order date for display
 */
export function formatOrderDate(dateString) {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
}

/**
 * Prepare order data before payment
 * Collects all necessary data from localStorage for saving after payment
 */
export function prepareGuestOrderData() {
  try {
    const guestProducts = JSON.parse(localStorage.getItem('guest_cart_products') || '[]');
    const guestMagazines = JSON.parse(localStorage.getItem('guest_cart_magazines') || '[]');
    const guestEvents = JSON.parse(localStorage.getItem('guest_cart_events') || '[]');
    const guestBillingArray = JSON.parse(localStorage.getItem('guest_billing_address') || '[]');
    const guestShippingArray = JSON.parse(localStorage.getItem('guest_shipping_address') || '[]');
    
    // Convert address arrays to objects
    const convertToObject = (arr) => {
      const obj = {};
      arr.forEach(item => {
        const key = item.name.replace('shipping_', '');
        obj[key] = item.value;
      });
      return obj;
    };
    
    const billingAddress = convertToObject(guestBillingArray);
    const shippingAddress = convertToObject(guestShippingArray);
    
    // Calculate total
    const productTotal = guestProducts.reduce((sum, p) => sum + (parseFloat(p.price) * parseInt(p.quantity)), 0);
    const magazineTotal = guestMagazines.reduce((sum, m) => sum + parseFloat(m.donation_contribution || m.price || 0), 0);
    const eventTotal = guestEvents.reduce((sum, e) => {
      const parentPrice = (parseFloat(e.price) || 0) * (parseInt(e.parent) || 0);
      const childPrice = (parseFloat(e.child_price) || parseFloat(e.price) || 0) * (parseInt(e.child) || 0);
      return sum + parentPrice + childPrice;
    }, 0);
    
    const total = productTotal + magazineTotal + eventTotal;
    
    return {
      products: guestProducts,
      magazines: guestMagazines,
      events: guestEvents,
      machineTestEvents: [],
      billing_address: billingAddress,
      shipping_address: shippingAddress,
      total_price: total.toString(),
      delivery_type: localStorage.getItem('selected_delivery_type') || '1',
      shipping_charges: localStorage.getItem('selected_shipping_charges') || '0'
    };
  } catch (error) {
    console.error('Error preparing guest order data:', error);
    return null;
  }
}

/**
 * Save pending order before payment
 * This order will be updated with transaction details after payment success
 */
export function savePendingGuestOrder(orderData) {
  try {
    localStorage.setItem('pending_guest_order', JSON.stringify(orderData));
    console.log('Pending guest order saved:', orderData);
    return true;
  } catch (error) {
    console.error('Error saving pending guest order:', error);
    return false;
  }
}

/**
 * Get pending order and update with payment response
 */
export async function completePendingGuestOrder(paymentResponse) {
  try {
    const pendingOrder = JSON.parse(localStorage.getItem('pending_guest_order') || 'null');
    
    if (!pendingOrder) {
      console.warn('No pending guest order found');
      return false;
    }
    
    // Update with payment details
    pendingOrder.order_id = paymentResponse.order_id;
    pendingOrder.order_id_main = paymentResponse.order_id;
    pendingOrder.transaction_id = paymentResponse.transaction_id || paymentResponse.tracking_id;
    pendingOrder.status = paymentResponse.order_status || 'Success';
    pendingOrder.payment_mode = paymentResponse.payment_mode || 'Online';
    pendingOrder.created_on = new Date().toISOString();
    
    // Save to orders history
    const saved = saveGuestOrder(pendingOrder);
    
    if (saved) {
      // Clear pending order
      localStorage.removeItem('pending_guest_order');
      console.log('Pending guest order completed and saved');
      
      // Send confirmation email to guest user
      // For guests, user_id is stored as 'guest_user_id_temp' to avoid showing logged-in UI
      const userId = localStorage.getItem('user_id') || localStorage.getItem('guest_user_id_temp');
      const email = pendingOrder.billing_address?.email || '';
      const firstName = pendingOrder.billing_address?.fname || 
                        pendingOrder.billing_address?.firstname || 
                        pendingOrder.billing_address?.first_name || '';
      
      console.log('=== GUEST ORDER EMAIL DEBUG ===');
      console.log('LocalStorage Check:', {
        user_id: localStorage.getItem('user_id'),
        guest_user_id_temp: localStorage.getItem('guest_user_id_temp'),
        isGuestAutoCreated: localStorage.getItem('isGuestAutoCreated'),
        final_userId: userId
      });
      console.log('Pending Order Data:', {
        billing_address: pendingOrder.billing_address,
        order_id: paymentResponse.order_id
      });
      console.log('Extracted Email Data:', {
        email: email,
        firstName: firstName,
        userId: userId,
        orderIdMain: paymentResponse.order_id
      });
      
      if (email && userId && paymentResponse.order_id) {
        console.log('Sending order confirmation email to guest user...');
        console.log('Email API will be called with:', {
          email: email,
          orderIdMain: paymentResponse.order_id,
          firstName: firstName,
          userId: userId
        });
        
        const result = await sendGuestOrderConfirmationEmail({
          email: email,
          orderIdMain: paymentResponse.order_id,
          firstName: firstName,
          userId: userId
        });
        
        console.log('Email API Response:', result);
        
        if (result.success) {
          console.log('Email sent successfully to:', email);
          // Mark email as sent to prevent duplicate calls
          localStorage.setItem('guest_email_sent', 'true');
        } else {
          console.warn('Email failed:', result.message);
        }
      } else {
        console.error('MISSING DATA - Email NOT sent:', {
          has_email: !!email,
          email_value: email,
          has_userId: !!userId,
          userId_value: userId,
          has_orderId: !!paymentResponse.order_id,
          orderId_value: paymentResponse.order_id
        });
      }
    }
    
    return saved;
  } catch (error) {
    console.error('Error completing pending guest order:', error);
    return false;
  }
}

